import { useState, useEffect, useCallback, useRef } from 'react'
import { Search, Scissors, Camera, Film, Tag, ChevronLeft, ChevronRight, Download, Link2, Play, Copy, Plus } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import { readCache, writeCache, invalidate as invalidateMuxCache } from '../../../lib/muxAssetsCache'
import CreateCatalogFromClipModal from './CreateCatalogFromClipModal'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import './MuxStreamEditor.css'

function parseTimeToSeconds(input) {
  if (!input && input !== 0) return null
  const str = String(input).trim()
  if (!str) return null
  const parts = str.split(':').map(Number)
  if (parts.some(isNaN)) return null
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2]
  if (parts.length === 2) return parts[0] * 60 + parts[1]
  return parseFloat(str)
}

function formatDuration(secs) {
  if (!secs && secs !== 0) return '—'
  const s = Math.round(secs)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
  return `${m}:${sec.toString().padStart(2, '0')}`
}

function formatDate(val) {
  if (!val) return '—'
  let num = typeof val === 'string' ? Number(val) : val
  let ts = !isNaN(num) && num > 0 ? (num < 1e12 ? num * 1000 : num) : val
  const d = new Date(ts)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function MuxStreamEditor() {
  const { api } = useAuth()

  // Asset list
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [allTags, setAllTags] = useState([])
  const [activeTag, setActiveTag] = useState('')
  const [status, setStatus] = useState('')

  // Selected asset
  const [selected, setSelected] = useState(null)
  const [activeTab, setActiveTab] = useState('clip')

  // Player
  const playerRef = useRef(null)
  const hlsUrlRef = useRef(null)
  const playbackIdRef = useRef(null)

  // Clip state
  const [clipStart, setClipStart] = useState('')
  const [clipEnd, setClipEnd] = useState('')
  const [clipTitle, setClipTitle] = useState('')
  const [clipping, setClipping] = useState(false)
  const [clipResult, setClipResult] = useState(null)

  // Thumbnail state
  const [thumbTime, setThumbTime] = useState('')
  const [thumbPreviewUrl, setThumbPreviewUrl] = useState(null)
  const [capturing, setCapturing] = useState(false)
  const [thumbResult, setThumbResult] = useState(null)

  // Instant Clip state
  const [instantStart, setInstantStart] = useState('')
  const [instantEnd, setInstantEnd] = useState('')
  const [instantUrl, setInstantUrl] = useState(null)
  const [instantCopied, setInstantCopied] = useState(false)

  // "Create catalog item" modal — set by either clip or instant-clip path.
  // Holds { initialTitle, sourceInfo, buildPayload } when open, null otherwise.
  const [catalogModal, setCatalogModal] = useState(null)

  // GIF state
  const [gifStart, setGifStart] = useState('')
  const [gifDuration, setGifDuration] = useState('5')
  const [gifPreviewUrl, setGifPreviewUrl] = useState(null)

  const [tagFilterIds, setTagFilterIds] = useState(null)

  // Load assets — stale-while-revalidate via sessionStorage shared with MuxStreamManager
  const loadAssets = useCallback(async () => {
    const cached = readCache()
    if (cached) {
      setAssets(cached)
      setLoading(false)
    } else {
      setLoading(true)
    }
    try {
      const data = await api('/mux/assets')
      const list = data.data || data || []
      setAssets(list)
      writeCache(list)
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setLoading(false)
  }, [api])

  useEffect(() => {
    loadAssets()
    api('/tags').then(d => setAllTags(d.tags || [])).catch(() => {})
  }, [loadAssets, api])

  // Tag filter: fetch matching IDs when tag changes
  useEffect(() => {
    if (!activeTag) { setTagFilterIds(null); return }
    api(`/tags/filter?tag=${encodeURIComponent(activeTag)}`)
      .then(d => {
        const tbtvIds = new Set((d.items || d.results || []).map(r => r.tbtv_id).filter(Boolean))
        const archiveKeys = new Set((d.archive_keys || []).filter(Boolean))
        setTagFilterIds({ tbtvIds, archiveKeys })
      })
      .catch(() => setTagFilterIds({ tbtvIds: new Set(), archiveKeys: new Set() }))
  }, [activeTag, api])

  // Cleanup player on unmount
  useEffect(() => {
    return () => {
      if (playerRef.current) { playerRef.current.dispose(); playerRef.current = null }
    }
  }, [])

  // Arrow keys: skip ±10s in the player
  useEffect(() => {
    function handleKeyDown(e) {
      if (!playerRef.current) return
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        playerRef.current.currentTime(Math.max(0, playerRef.current.currentTime() - 10))
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        playerRef.current.currentTime(playerRef.current.currentTime() + 10)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  function getPlaybackId(asset) {
    return asset?.playback_ids?.[0]?.id || null
  }

  function getTitle(asset) {
    return asset?.meta?.title || asset?.passthrough || asset?.id
  }

  // Player callback ref
  const playerWrapperRef = useCallback((node) => {
    if (!node) return
    // Clear existing
    if (playerRef.current) { playerRef.current.dispose(); playerRef.current = null }
    node.innerHTML = ''
    const videoEl = document.createElement('video')
    videoEl.className = 'video-js vjs-default-skin'
    node.appendChild(videoEl)
    const player = videojs(videoEl, {
      controls: true,
      autoplay: true,
      fluid: true,
      sources: [{ src: hlsUrlRef.current, type: 'application/x-mpegURL' }]
    })
    playerRef.current = player

    // Add Mux storyboard thumbnails for scrubber hover preview
    if (playbackIdRef.current) {
      const pid = playbackIdRef.current

      // Listen for progress bar hover and show thumbnail preview
      player.ready(() => {
        const progressControl = player.controlBar?.progressControl?.el()
        if (!progressControl) return

        let thumbEl = null

        progressControl.addEventListener('mousemove', (e) => {
          const rect = progressControl.getBoundingClientRect()
          const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
          const time = Math.floor(pct * (player.duration() || 0))

          if (!thumbEl) {
            thumbEl = document.createElement('div')
            thumbEl.className = 'mux-scrub-thumb'
            progressControl.appendChild(thumbEl)
          }

          thumbEl.style.left = `${pct * 100}%`
          thumbEl.style.backgroundImage = `url(https://image.mux.com/${pid}/thumbnail.jpg?time=${time}&width=160&height=90)`
          thumbEl.style.display = 'block'
          thumbEl.textContent = formatDuration(time)
        })

        progressControl.addEventListener('mouseleave', () => {
          if (thumbEl) thumbEl.style.display = 'none'
        })
      })
    }
  }, [])

  function selectAsset(asset) {
    const pid = getPlaybackId(asset)
    if (!pid) { setStatus('Asset has no playback ID'); return }

    const newUrl = `https://stream.mux.com/${pid}.m3u8`
    hlsUrlRef.current = newUrl
    playbackIdRef.current = pid

    // If player exists, change source directly (don't dispose — callback ref won't re-fire)
    if (playerRef.current) {
      playerRef.current.src({ src: newUrl, type: 'application/x-mpegURL' })
      playerRef.current.play().catch(() => {})
    }

    setSelected(asset)
    setClipStart('')
    setClipEnd('')
    setClipTitle(`${getTitle(asset)} - Clip`)
    setClipResult(null)
    setThumbTime('')
    setThumbPreviewUrl(null)
    setThumbResult(null)
    setGifStart('')
    setGifPreviewUrl(null)
    setInstantStart('')
    setInstantEnd('')
    setInstantUrl(null)
    setStatus('')
  }

  // Get current player time
  function getCurrentTime() {
    if (playerRef.current) return Math.floor(playerRef.current.currentTime())
    return 0
  }

  // ── CLIP ──
  async function handleCreateClip() {
    const start = parseTimeToSeconds(clipStart)
    const end = parseTimeToSeconds(clipEnd)
    if (start == null || end == null) { setStatus('Invalid start/end time'); return }
    if (end <= start) { setStatus('End time must be after start time'); return }

    setClipping(true)
    setClipResult(null)
    setStatus('Creating clip...')
    try {
      const data = await api('/mux/clip', {
        method: 'POST',
        body: JSON.stringify({
          sourceAssetId: selected.id,
          startTime: start,
          endTime: end,
          title: clipTitle.trim() || undefined,
        })
      })
      setClipResult(data)
      setStatus(`Clip created: ${data.title}`)
      // New asset added on Mux side — invalidate cache so a return to the
      // Manager (or a fresh editor mount) sees the clip.
      invalidateMuxCache()
    } catch (e) {
      setStatus(`Clip failed: ${e.message}`)
    }
    setClipping(false)
  }

  // ── CREATE CATALOG ITEM (from clip or instant clip) ──
  function openCatalogFromClip() {
    if (!clipResult) return
    const pid = clipResult.playbackId
    const tags = clipResult.copiedTags || []
    const muxThumb = pid ? `https://image.mux.com/${pid}/thumbnail.jpg?width=1280&height=720&time=5` : ''
    setCatalogModal({
      initialTitle: clipResult.title || '',
      sourceInfo: `From new Mux clip · ${formatDuration(clipResult.duration)}${tags.length ? ` · ${tags.length} tag${tags.length !== 1 ? 's' : ''} will be applied` : ''}`,
      buildPayload: (title, thumbnailOverride) => ({
        title,
        media_url: pid ? `https://stream.mux.com/${pid}.m3u8` : (clipResult.hlsUrl || ''),
        video_type: 'MUX',
        quality: 'FHD',
        duration: Math.round(clipResult.duration || 0),
        short_description: '',
        long_description: '',
        thumbnail_url: thumbnailOverride || muxThumb,
        release_date: null,
        status: 'draft',
        tags,
        youtube_id: null,
        cf_stream_uid: null,
      }),
    })
  }

  function openCatalogFromInstantClip() {
    if (!instantUrl || !selected) return
    const start = parseTimeToSeconds(instantStart)
    const end = parseTimeToSeconds(instantEnd)
    if (start == null || end == null) return
    const duration = Math.round(end - start)
    const pid = getPlaybackId(selected)
    const sourceTitle = selected.meta?.title || selected.passthrough || selected.id
    const muxThumb = pid ? `https://image.mux.com/${pid}/thumbnail.jpg?width=1280&height=720&time=${start}` : ''
    setCatalogModal({
      initialTitle: `${sourceTitle} — Clip ${formatDuration(start)}–${formatDuration(end)}`,
      sourceInfo: `Instant clip from "${sourceTitle}" (${formatDuration(start)}–${formatDuration(end)} · ${formatDuration(duration)})`,
      buildPayload: (title, thumbnailOverride) => ({
        title,
        media_url: instantUrl,
        video_type: 'HLS',
        quality: 'FHD',
        duration,
        short_description: '',
        long_description: '',
        thumbnail_url: thumbnailOverride || muxThumb,
        release_date: null,
        status: 'draft',
        tags: [],
        youtube_id: null,
        cf_stream_uid: null,
      }),
    })
  }

  // ── INSTANT CLIP ──
  function generateInstantClipUrl() {
    const start = parseTimeToSeconds(instantStart)
    const end = parseTimeToSeconds(instantEnd)
    if (start == null || end == null || end <= start) { setStatus('Invalid start/end time'); return }
    const pid = getPlaybackId(selected)
    if (!pid) { setStatus('No playback ID'); return }
    const url = `https://stream.mux.com/${pid}.m3u8?asset_start_time=${start}&asset_end_time=${end}`
    setInstantUrl(url)
    setInstantCopied(false)
    setStatus(`Instant clip URL generated (${formatDuration(end - start)})`)
  }

  function copyInstantUrl() {
    if (!instantUrl) return
    navigator.clipboard.writeText(instantUrl).then(() => {
      setInstantCopied(true)
      setTimeout(() => setInstantCopied(false), 2000)
    })
  }

  function previewInstantClip() {
    if (!instantUrl || !playerRef.current) return
    playerRef.current.src({ src: instantUrl, type: 'application/x-mpegURL' })
    playerRef.current.play().catch(() => {})
  }

  // ── THUMBNAIL ──
  function previewThumbnail() {
    const t = parseTimeToSeconds(thumbTime)
    if (t == null) { setStatus('Invalid time'); return }
    const pid = getPlaybackId(selected)
    setThumbPreviewUrl(`https://image.mux.com/${pid}/thumbnail.jpg?time=${t}&width=640`)
  }

  async function handleCaptureThumbnail() {
    const t = parseTimeToSeconds(thumbTime)
    if (t == null) { setStatus('Invalid time'); return }

    setCapturing(true)
    setThumbResult(null)
    setStatus('Capturing and uploading...')
    try {
      const data = await api('/mux/thumbnail-capture', {
        method: 'POST',
        body: JSON.stringify({
          playbackId: getPlaybackId(selected),
          time: t,
          title: `${getTitle(selected)} - Frame ${formatDuration(t)}`,
        })
      })
      setThumbResult(data)
      setStatus('Thumbnail captured and uploaded to Images')
    } catch (e) {
      setStatus(`Capture failed: ${e.message}`)
    }
    setCapturing(false)
  }

  // ── GIF ──
  function previewGif() {
    const t = parseTimeToSeconds(gifStart)
    const dur = parseFloat(gifDuration)
    if (t == null || isNaN(dur)) { setStatus('Invalid GIF time/duration'); return }
    const pid = getPlaybackId(selected)
    setGifPreviewUrl(`https://image.mux.com/${pid}/animated.gif?start=${t}&end=${t + dur}&width=480`)
  }

  // Filter assets by search + tag
  const filtered = assets.filter(a => {
    const title = getTitle(a).toLowerCase()
    if (searchTerm && !title.includes(searchTerm.toLowerCase())) return false
    if (tagFilterIds) {
      const tbtvId = a.meta?.external_id
      const archiveKey = a.passthrough
      if (tbtvId && tagFilterIds.tbtvIds.has(tbtvId)) return true
      if (archiveKey && tagFilterIds.archiveKeys.has(archiveKey)) return true
      return false
    }
    return true
  })

  return (
    <div className="mux-editor">
      <div className="mux-editor-header">
        <h1><Scissors size={20} /> Mux Stream Editor</h1>
        <span className="mux-editor-status">{status}</span>
      </div>

      <div className="mux-editor-layout">
        {/* Left: Video list */}
        <div className="mux-editor-sidebar">
          <div className="mux-editor-search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search assets..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <select
              value={activeTag}
              onChange={e => setActiveTag(e.target.value)}
              style={{ padding: '6px 8px', fontSize: 12, borderRadius: 4, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)' }}
            >
              <option value="">All Tags</option>
              {allTags.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div className="mux-editor-list">
            {loading ? (
              <div className="mux-editor-empty">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="mux-editor-empty">No assets found</div>
            ) : filtered.map(asset => (
              <div
                key={asset.id}
                className={`mux-editor-item ${selected?.id === asset.id ? 'selected' : ''}`}
                onClick={() => selectAsset(asset)}
              >
                <img
                  src={`https://image.mux.com/${getPlaybackId(asset)}/thumbnail.jpg?width=120&height=68&time=5`}
                  alt=""
                  className="mux-editor-thumb"
                  loading="lazy"
                  decoding="async"
                  onError={e => e.target.style.display = 'none'}
                />
                <div className="mux-editor-item-info">
                  <div className="mux-editor-item-title">{getTitle(asset)}</div>
                  <div className="mux-editor-item-meta">
                    {formatDuration(asset.duration)} · {formatDate(asset.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Player + Editor */}
        <div className="mux-editor-main">
          {!selected ? (
            <div className="mux-editor-placeholder">
              <Scissors size={48} style={{ opacity: 0.2 }} />
              <p>Select a video to edit</p>
            </div>
          ) : (
            <>
              {/* Player */}
              <div className="mux-editor-player">
                <div ref={playerWrapperRef} className="mux-editor-player-wrap" />
                <div className="mux-editor-player-info">
                  <span className="mux-editor-player-title">{getTitle(selected)}</span>
                  <span className="mux-editor-player-duration">{formatDuration(selected.duration)}</span>
                </div>
              </div>

              {/* Tabs */}
              <div className="mux-editor-tabs">
                <button className={`mux-editor-tab ${activeTab === 'clip' ? 'active' : ''}`} onClick={() => setActiveTab('clip')}>
                  <Scissors size={14} /> Clip
                </button>
                <button className={`mux-editor-tab ${activeTab === 'thumbnail' ? 'active' : ''}`} onClick={() => setActiveTab('thumbnail')}>
                  <Camera size={14} /> Thumbnail
                </button>
                <button className={`mux-editor-tab ${activeTab === 'gif' ? 'active' : ''}`} onClick={() => setActiveTab('gif')}>
                  <Film size={14} /> GIF
                </button>
                <button className={`mux-editor-tab ${activeTab === 'instant' ? 'active' : ''}`} onClick={() => setActiveTab('instant')}>
                  <Link2 size={14} /> Instant Clip
                </button>
              </div>

              {/* Tab content */}
              <div className="mux-editor-panel">
                {activeTab === 'clip' && (
                  <div className="mux-editor-clip">
                    <div className="mux-editor-field-row">
                      <div className="mux-editor-field">
                        <label>Start Time</label>
                        <div className="mux-editor-time-input">
                          <input type="text" value={clipStart} onChange={e => setClipStart(e.target.value)} placeholder="0:00" />
                          <button className="mux-editor-btn-sm" onClick={() => setClipStart(String(getCurrentTime()))} title="Use current time">Now</button>
                        </div>
                      </div>
                      <div className="mux-editor-field">
                        <label>End Time</label>
                        <div className="mux-editor-time-input">
                          <input type="text" value={clipEnd} onChange={e => setClipEnd(e.target.value)} placeholder="0:30" />
                          <button className="mux-editor-btn-sm" onClick={() => setClipEnd(String(getCurrentTime()))} title="Use current time">Now</button>
                        </div>
                      </div>
                    </div>
                    {clipStart && clipEnd && parseTimeToSeconds(clipStart) != null && parseTimeToSeconds(clipEnd) != null && (
                      <div className="mux-editor-clip-duration">
                        Clip duration: {formatDuration(parseTimeToSeconds(clipEnd) - parseTimeToSeconds(clipStart))}
                      </div>
                    )}
                    <div className="mux-editor-field">
                      <label>Clip Title</label>
                      <input type="text" value={clipTitle} onChange={e => setClipTitle(e.target.value)} />
                    </div>
                    <button className="mux-editor-btn-primary" onClick={handleCreateClip} disabled={clipping}>
                      <Scissors size={14} /> {clipping ? 'Creating...' : 'Create Clip'}
                    </button>
                    {clipResult && (
                      <div className="mux-editor-result success">
                        <div className="mux-editor-result-title">Clip created: {clipResult.title}</div>
                        <div className="mux-editor-result-meta">
                          Asset: {clipResult.assetId} · Duration: {formatDuration(clipResult.duration)}
                        </div>
                        {clipResult.copiedTags?.length > 0 && (
                          <div className="mux-editor-result-tags">
                            <Tag size={10} /> Tags copied: {clipResult.copiedTags.join(', ')}
                          </div>
                        )}
                        <div style={{ marginTop: 8 }}>
                          <button className="mux-editor-btn-secondary" onClick={openCatalogFromClip}>
                            <Plus size={14} /> Create Catalog Item
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'thumbnail' && (
                  <div className="mux-editor-thumbnail">
                    <div className="mux-editor-field-row">
                      <div className="mux-editor-field" style={{ flex: 1 }}>
                        <label>Timecode</label>
                        <div className="mux-editor-time-input">
                          <input type="text" value={thumbTime} onChange={e => setThumbTime(e.target.value)} placeholder="0:00" />
                          <button className="mux-editor-btn-sm" onClick={() => setThumbTime(String(getCurrentTime()))}>Now</button>
                        </div>
                      </div>
                      <button className="mux-editor-btn-secondary" onClick={previewThumbnail} style={{ alignSelf: 'flex-end' }}>
                        Preview
                      </button>
                      <button className="mux-editor-btn-primary" onClick={handleCaptureThumbnail} disabled={capturing} style={{ alignSelf: 'flex-end' }}>
                        <Camera size={14} /> {capturing ? 'Uploading...' : 'Capture & Upload'}
                      </button>
                    </div>
                    {thumbPreviewUrl && (
                      <div className="mux-editor-preview-img">
                        <img src={thumbPreviewUrl} alt="Frame preview" />
                      </div>
                    )}
                    {thumbResult && (
                      <div className="mux-editor-result success">
                        <div className="mux-editor-result-title">Thumbnail uploaded to Cloudflare Images</div>
                        <div className="mux-editor-result-meta">Image ID: {thumbResult.imageId}</div>
                        {thumbResult.imageUrl && (
                          <a href={thumbResult.imageUrl} target="_blank" rel="noopener noreferrer" className="mux-editor-result-link">View Image</a>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'gif' && (
                  <div className="mux-editor-gif">
                    <div className="mux-editor-field-row">
                      <div className="mux-editor-field">
                        <label>Start Time</label>
                        <div className="mux-editor-time-input">
                          <input type="text" value={gifStart} onChange={e => setGifStart(e.target.value)} placeholder="0:00" />
                          <button className="mux-editor-btn-sm" onClick={() => setGifStart(String(getCurrentTime()))}>Now</button>
                        </div>
                      </div>
                      <div className="mux-editor-field">
                        <label>Duration (sec, max 10)</label>
                        <input type="number" value={gifDuration} onChange={e => setGifDuration(e.target.value)} min={1} max={10} />
                      </div>
                    </div>
                    <div className="mux-editor-field-row">
                      <button className="mux-editor-btn-secondary" onClick={previewGif}>
                        Preview GIF
                      </button>
                      {gifPreviewUrl && (
                        <a href={gifPreviewUrl} download className="mux-editor-btn-secondary">
                          <Download size={14} /> Download GIF
                        </a>
                      )}
                    </div>
                    {gifPreviewUrl && (
                      <div className="mux-editor-preview-img">
                        <img src={gifPreviewUrl} alt="GIF preview" />
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'instant' && (
                  <div className="mux-editor-clip">
                    <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '0 0 12px' }}>
                      Generate a shareable HLS URL that plays only the selected portion. No new asset is created — works instantly via URL parameters.
                    </p>
                    <div className="mux-editor-field-row">
                      <div className="mux-editor-field">
                        <label>Start Time</label>
                        <div className="mux-editor-time-input">
                          <input type="text" value={instantStart} onChange={e => setInstantStart(e.target.value)} placeholder="0:00" />
                          <button className="mux-editor-btn-sm" onClick={() => setInstantStart(String(getCurrentTime()))} title="Use current time">Now</button>
                        </div>
                      </div>
                      <div className="mux-editor-field">
                        <label>End Time</label>
                        <div className="mux-editor-time-input">
                          <input type="text" value={instantEnd} onChange={e => setInstantEnd(e.target.value)} placeholder="0:30" />
                          <button className="mux-editor-btn-sm" onClick={() => setInstantEnd(String(getCurrentTime()))} title="Use current time">Now</button>
                        </div>
                      </div>
                    </div>
                    {instantStart && instantEnd && parseTimeToSeconds(instantStart) != null && parseTimeToSeconds(instantEnd) != null && (
                      <div className="mux-editor-clip-duration">
                        Clip duration: {formatDuration(parseTimeToSeconds(instantEnd) - parseTimeToSeconds(instantStart))}
                      </div>
                    )}
                    <div className="mux-editor-field-row">
                      <button className="mux-editor-btn-primary" onClick={generateInstantClipUrl}>
                        <Link2 size={14} /> Generate URL
                      </button>
                      {instantUrl && (
                        <>
                          <button className="mux-editor-btn-secondary" onClick={previewInstantClip}>
                            <Play size={14} /> Preview in Player
                          </button>
                          <button className="mux-editor-btn-secondary" onClick={copyInstantUrl}>
                            <Copy size={14} /> {instantCopied ? 'Copied!' : 'Copy URL'}
                          </button>
                        </>
                      )}
                    </div>
                    {instantUrl && (
                      <div className="mux-editor-result success" style={{ wordBreak: 'break-all' }}>
                        <div className="mux-editor-result-title">Instant Clip URL</div>
                        <code style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{instantUrl}</code>
                        <div style={{ marginTop: 8 }}>
                          <button className="mux-editor-btn-secondary" onClick={openCatalogFromInstantClip}>
                            <Plus size={14} /> Create Catalog Item
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {catalogModal && (
        <CreateCatalogFromClipModal
          initialTitle={catalogModal.initialTitle}
          sourceInfo={catalogModal.sourceInfo}
          buildPayload={catalogModal.buildPayload}
          onClose={() => setCatalogModal(null)}
          onComplete={() => {
            setStatus('✅ Catalog item created (draft)')
          }}
        />
      )}
    </div>
  )
}
